// components/EnhancedAnalyticsDashboard.tsx
// Enhanced COPPA-compliant analytics dashboard with app-wide metrics

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { StatsCard } from './StatsCard';
import SystemHealthWidget from './SystemHealthWidget';
import ActiveAlerts from './ActiveAlerts';
import AppUsageChart from './AppUsageChart';
import {
    fetchDashboardAnalytics,
    getAppUsageStats,
    getBarkBoardStats,
    getFriendsStats,
    getGamesStats,
    getCreatorToolsStats,
    getPollsSparksStats,
} from '../services/analytics';
import type {
    DashboardAnalytics,
    AppUsageStats,
    BarkBoardStats,
    FriendsStats,
    GamesStats,
    CreatorToolsStats,
    PollsSparksStats,
} from '../types';
import './AnalyticsDashboard.css';

type DateRange = '7d' | '30d' | '90d';

export const AnalyticsDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dateRange, setDateRange] = useState<DateRange>('7d');

    // Analytics data states
    const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
    const [appUsage, setAppUsage] = useState<AppUsageStats[]>([]);
    const [barkBoard, setBarkBoard] = useState<BarkBoardStats[]>([]);
    const [friends, setFriends] = useState<FriendsStats[]>([]);
    const [games, setGames] = useState<GamesStats[]>([]);
    const [creatorTools, setCreatorTools] = useState<CreatorToolsStats[]>([]);
    const [pollsSparks, setPollsSparks] = useState<PollsSparksStats[]>([]);


    const getDateRange = useCallback((): { start: Date; end: Date } => {
        const end = new Date();
        const start = new Date();

        switch (dateRange) {
            case '7d':
                start.setDate(end.getDate() - 7);
                break;
            case '30d':
                start.setDate(end.getDate() - 30);
                break;
            case '90d':
                start.setDate(end.getDate() - 90);
                break;
        }

        return { start, end };
    }, [dateRange]);

    const loadAnalytics = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const { start, end } = getDateRange();

            // Load all analytics data in parallel
            const [
                dashboardData,
                appUsageData,
                barkBoardData,
                friendsData,
                gamesData,
                creatorToolsData,
                pollsSparksData,
            ] = await Promise.all([
                fetchDashboardAnalytics(start, end),
                getAppUsageStats(start, end),
                getBarkBoardStats(start, end),
                getFriendsStats(start, end),
                getGamesStats(start, end),
                getCreatorToolsStats(start, end),
                getPollsSparksStats(start, end),
            ]);

            setAnalytics(dashboardData);
            setAppUsage(appUsageData);
            setBarkBoard(barkBoardData);
            setFriends(friendsData);
            setGames(gamesData);
            setCreatorTools(creatorToolsData);
            setPollsSparks(pollsSparksData);
        } catch (err: any) {
            console.error('Error loading analytics:', err);
            setError('Failed to load analytics data');
        } finally {
            setLoading(false);
        }
    }, [getDateRange]);

    useEffect(() => {
        loadAnalytics();
    }, [loadAnalytics]);

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    if (loading) {
        return (
            <div className="analytics-container">
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading analytics...</p>
                </div>
            </div>
        );
    }

    if (error || !analytics) {
        return (
            <div className="analytics-container">
                <div className="error-state">
                    <p>{error || 'Failed to load analytics'}</p>
                    <button onClick={loadAnalytics} className="retry-button">Retry</button>
                </div>
            </div>
        );
    }

    // Calculate summary stats for app usage
    const totalLaunches = appUsage.reduce((sum, d) => sum + d.totalLaunches, 0);
    const totalUsers = Math.max(...appUsage.map(d => d.uniqueUsers), 0);

    // Calculate summary stats for BarkBoard
    const totalPostsCreated = barkBoard.reduce((sum, d) => sum + d.postsCreated.total, 0);
    const totalCompliments = barkBoard.reduce((sum, d) => sum + d.complimentsGiven.total, 0);

    // Calculate summary stats for Friends
    const totalDiscoveries = friends.reduce((sum, d) => sum + d.discoveries.total, 0);
    const totalFriendRequests = friends.reduce((sum, d) => sum + d.friendRequestsAccepted, 0);

    // Calculate summary stats for Games
    const totalGamesPlayed = games.reduce((sum, d) => {
        return sum + Object.values(d.gamesPlayed).reduce((a, b) => a + b, 0);
    }, 0);

    return (
        <div className="analytics-container">
            <header className="analytics-header">
                <div className="header-content">
                    <div>
                        <h1>📊 Omore Analytics</h1>
                        <p className="header-subtitle">COPPA-compliant app-wide metrics</p>
                    </div>
                    <div className="header-actions">
                        <button onClick={() => navigate('/dashboard')} className="back-button">
                            ← Back to Dashboard
                        </button>
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">
                            Sign Out
                        </button>
                    </div>
                </div>
            </header>

            <div className="analytics-main">
                <div className="date-range-selector">
                    <button
                        className={`range-button ${dateRange === '7d' ? 'active' : ''}`}
                        onClick={() => setDateRange('7d')}
                    >
                        Last 7 Days
                    </button>
                    <button
                        className={`range-button ${dateRange === '30d' ? 'active' : ''}`}
                        onClick={() => setDateRange('30d')}
                    >
                        Last 30 Days
                    </button>
                    <button
                        className={`range-button ${dateRange === '90d' ? 'active' : ''}`}
                        onClick={() => setDateRange('90d')}
                    >
                        Last 90 Days
                    </button>
                    <button onClick={loadAnalytics} className="refresh-button" title="Refresh Data">
                        🔄 Refresh
                    </button>
                </div>

                {/* System Health - Always at top */}
                <div className="system-health-section">
                    <SystemHealthWidget />
                    <ActiveAlerts />
                </div>

                {/* App Usage Section */}
                <section className="analytics-section">
                    <h2>📱 App Usage</h2>
                    <div className="stats-grid">
                        <StatsCard
                            title="Total Launches"
                            value={totalLaunches}
                            icon="🚀"
                            color="blue"
                            subtitle={`${totalUsers} unique users`}
                        />
                        <StatsCard
                            title="Active Users"
                            value={totalUsers}
                            icon="👥"
                            color="green"
                            subtitle="Unique anonymized IDs"
                        />
                    </div>
                    <AppUsageChart data={appUsage} />
                </section>

                {/* BarkBoard Section */}
                <section className="analytics-section">
                    <h2>📝 BarkBoard Activity</h2>
                    <div className="stats-grid">
                        <StatsCard
                            title="Posts Created"
                            value={totalPostsCreated}
                            icon="✍️"
                            color="purple"
                            subtitle="User-generated content"
                        />
                        <StatsCard
                            title="Compliments Given"
                            value={totalCompliments}
                            icon="💬"
                            color="green"
                            subtitle="Positive engagement"
                        />
                    </div>
                    {barkBoard.length > 0 && (
                        <div className="simple-breakdown">
                            <h3>Posts by Type</h3>
                            <div className="breakdown-grid">
                                {Object.entries(
                                    barkBoard.reduce((acc, day) => {
                                        Object.entries(day.postsCreated.byType).forEach(([type, count]) => {
                                            acc[type] = (acc[type] || 0) + count;
                                        });
                                        return acc;
                                    }, {} as Record<string, number>)
                                ).map(([type, count]) => (
                                    <div key={type} className="breakdown-item">
                                        <span>{type}</span>
                                        <strong>{count}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </section>

                {/* Friends Section */}
                {friends.length > 0 && (
                    <section className="analytics-section">
                        <h2>👋 Friend Discovery</h2>
                        <div className="stats-grid">
                            <StatsCard
                                title="Discoveries"
                                value={totalDiscoveries}
                                icon="🔍"
                                color="blue"
                                subtitle="Friend discoveries"
                            />
                            <StatsCard
                                title="Friend Requests"
                                value={totalFriendRequests}
                                icon="✅"
                                color="green"
                                subtitle="Accepted requests"
                            />
                        </div>
                    </section>
                )}

                {/* Games Section */}
                {games.length > 0 && totalGamesPlayed > 0 && (
                    <section className="analytics-section">
                        <h2>🎮 Games</h2>
                        <StatsCard
                            title="Games Played"
                            value={totalGamesPlayed}
                            icon="🎲"
                            color="yellow"
                            subtitle="Total game sessions"
                        />
                    </section>
                )}

                {/* Creator Tools Section */}
                {creatorTools.length > 0 && (
                    <section className="analytics-section">
                        <h2>🎨 Creator Tools</h2>
                        <div className="simple-breakdown">
                            <h3>Tool Usage</h3>
                            <div className="breakdown-grid">
                                {Object.entries(
                                    creatorTools.reduce((acc, day) => {
                                        Object.entries(day.completions).forEach(([tool, count]) => {
                                            acc[tool] = (acc[tool] || 0) + count;
                                        });
                                        return acc;
                                    }, {} as Record<string, number>)
                                ).map(([tool, count]) => (
                                    <div key={tool} className="breakdown-item">
                                        <span>{tool}</span>
                                        <strong>{count}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Polls & Sparks Section */}
                {pollsSparks.length > 0 && (
                    <section className="analytics-section">
                        <h2>💭 Polls & Sparks</h2>
                        <div className="stats-grid">
                            <StatsCard
                                title="Polls Completed"
                                value={pollsSparks.reduce((sum, d) => sum + d.pollsCompleted, 0)}
                                icon="📊"
                                color="blue"
                            />
                            <StatsCard
                                title="Sparks Revealed"
                                value={pollsSparks.reduce((sum, d) => sum + d.sparksRevealed, 0)}
                                icon="✨"
                                color="purple"
                            />
                        </div>
                    </section>
                )}

                {/* Original BarkBoard Moderation Section */}
                <section className="overview-section">
                    <h2>🛡️ Moderation Overview</h2>
                    <div className="stats-grid">
                        <StatsCard
                            title="Moderation Queue"
                            value={analytics.currentQueueSize}
                            icon="⚠️"
                            color={analytics.currentQueueSize > 10 ? 'red' : 'yellow'}
                            subtitle={`${analytics.pendingReports} pending reports`}
                        />
                        <StatsCard
                            title="Total Reports"
                            value={analytics.totalReports}
                            icon="📋"
                            color="purple"
                            subtitle={`${analytics.resolvedReports} resolved`}
                        />
                        <StatsCard
                            title="Moderation Rate"
                            value={`${Math.round(analytics.moderationRate)}%`}
                            icon="✓"
                            color="green"
                            subtitle="Reports resolved"
                        />
                    </div>
                </section>

                <div className="coppa-notice">
                    <p>
                        🔒 <strong>COPPA Compliant:</strong> All metrics are aggregated and anonymized.
                        No individual user tracking or personal data collection.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AnalyticsDashboard;
